import React, { useState, useEffect } from 'react';
import { getStorage, ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { collection, addDoc, getDocs, deleteDoc, doc, query, orderBy, limit, startAfter } from 'firebase/firestore';
import { db } from '../../../../../Config/firebase';
import { toast } from 'react-toastify';

interface Note {
    id: string;
    timestamp: string;
    notes: string;
    imageLinks?: string[];
    fileLinks?: Array<{
        url: string;
        name: string;
        size: number;
        uploadedAt: string;
    }>;
    createdAt: any;
}

interface NotesSectionProps {
    subscriptionId: string;
    providerId: string;
}

export default function NotesSection({ subscriptionId, providerId }: NotesSectionProps) {
    const [notes, setNotes] = useState<Note[]>([]);
    const [loading, setLoading] = useState(false);
    const [isAdding, setIsAdding] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [showAllNotes, setShowAllNotes] = useState(false);
    const [lastDoc, setLastDoc] = useState<any>(null);
    const [hasMore, setHasMore] = useState(true);
    
    const [newNote, setNewNote] = useState({
        notes: '',
        files: [] as File[]
    });
    const [noteError, setNoteError] = useState(false);

    // Load initial notes
    useEffect(() => {
        loadNotes();
    }, [subscriptionId, providerId]);

    const loadNotes = async (loadMore = false) => {
        if (!subscriptionId || !providerId) return;
        
        setLoading(true);
        try {
            const notesRef = collection(db, 'ServiceProviders', providerId, 'Subscriptions', subscriptionId, 'Notes');
            let q = query(notesRef, orderBy('createdAt', 'desc'), limit(5));
            
            if (loadMore && lastDoc) {
                q = query(notesRef, orderBy('createdAt', 'desc'), startAfter(lastDoc), limit(5));
            }
            
            const querySnapshot = await getDocs(q);
            const newNotes: Note[] = [];
            
            querySnapshot.forEach((doc) => {
                newNotes.push({
                    id: doc.id,
                    ...doc.data()
                } as Note);
            });
            
            if (loadMore) {
                setNotes(prev => [...prev, ...newNotes]);
            } else {
                setNotes(newNotes);
            }
            
            setLastDoc(querySnapshot.docs[querySnapshot.docs.length - 1]);
            setHasMore(querySnapshot.docs.length === 5);
            
        } catch (error) {
            console.error('Error loading notes:', error);
            toast.error('Failed to load notes');
        } finally {
            setLoading(false);
        }
    };

    const handleFileUpload = async (files: File[]): Promise<Array<{ url: string; name: string; size: number; uploadedAt: string }>> => {
        if (!files || files.length === 0) return [];
        
        const storage = getStorage();
        const uploadPromises = files.map(async (file) => {
            // Check file size (10MB limit)
            if (file.size > 10 * 1024 * 1024) {
                throw new Error(`File ${file.name} is too large. Maximum size is 10MB.`);
            }
            
            const fileExtension = file.name.split('.').pop();
            const fileName = `notes/${subscriptionId}/${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExtension}`;
            const storageRef = ref(storage, fileName);

            try {
                const snapshot = await uploadBytes(storageRef, file);
                const downloadURL = await getDownloadURL(snapshot.ref);
                return {
                    url: downloadURL,
                    name: file.name,
                    size: file.size,
                    uploadedAt: new Date().toISOString()
                };
            } catch (error) {
                console.error('Error uploading file:', error);
                throw error;
            }
        });

        return Promise.all(uploadPromises);
    };

    const handleAddNote = async () => {
        if (!newNote.notes.trim()) {
            setNoteError(true);
            toast.error('Please enter some notes');
            return;
        }

        setNoteError(false);
        setIsUploading(true);
        try {
            // Upload files if any
            const fileLinks = await handleFileUpload(newNote.files);

            // Create note document
            const noteData = {
                notes: newNote.notes,
                fileLinks: fileLinks,
                timestamp: new Date().toISOString(),
                createdAt: new Date()
            };

            const notesRef = collection(db, 'ServiceProviders', providerId, 'Subscriptions', subscriptionId, 'Notes');
            await addDoc(notesRef, noteData);

            // Reset form
            setNewNote({ notes: '', files: [] });
            setIsAdding(false);
            setNoteError(false);
            
            // Reload notes
            await loadNotes();
            
            toast.success('Note added successfully');
        } catch (error) {
            console.error('Error adding note:', error);
            toast.error('Failed to add note');
        } finally {
            setIsUploading(false);
        }
    };

    const handleDeleteNote = async (note: Note) => {
        try {
            const storage = getStorage();
            const deletePromises = [];

            // Delete images from storage
            if (note.imageLinks && note.imageLinks.length > 0) {
                const imageDeletePromises = note.imageLinks.map(async (imageUrl) => {
                    try {
                        const fileRef = ref(storage, imageUrl);
                        await deleteObject(fileRef);
                    } catch (error) {
                        console.error('Error deleting image:', error);
                    }
                });
                deletePromises.push(...imageDeletePromises);
            }

            // Delete files from storage
            if (note.fileLinks && note.fileLinks.length > 0) {
                const fileDeletePromises = note.fileLinks.map(async (file) => {
                    try {
                        const fileRef = ref(storage, file.url);
                        await deleteObject(fileRef);
                    } catch (error) {
                        console.error('Error deleting file:', error);
                    }
                });
                deletePromises.push(...fileDeletePromises);
            }

            await Promise.all(deletePromises);

            // Delete note document
            const noteRef = doc(db, 'ServiceProviders', providerId, 'Subscriptions', subscriptionId, 'Notes', note.id);
            await deleteDoc(noteRef);

            // Reload notes
            await loadNotes();
            
            toast.success('Note deleted successfully');
        } catch (error) {
            console.error('Error deleting note:', error);
            toast.error('Failed to delete note');
        }
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(e.target.files || []);
        
        // Check each file size (5MB limit per file)
        for (const file of files) {
            if (file.size > 5 * 1024 * 1024) {
                toast.error(`File "${file.name}" is too large. Maximum size is 5MB per file.`);
                e.target.value = '';
                return;
            }
        }
        
        setNewNote(prev => ({ ...prev, files: files }));
    };

    const formatDate = (timestamp: string) => {
        return new Date(timestamp).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatFileSize = (bytes: number) => {
        if (bytes < 1024) return bytes + ' B';
        if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
        return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    };

    // Function to render links in text
    const renderTextWithLinks = (text: string) => {
        const urlRegex = /(https?:\/\/[^\s]+)/g;
        const parts = text.split(urlRegex);
        
        return parts.map((part, index) => {
            if (part.match(urlRegex)) {
                return (
                    <a
                        key={index}
                        href={part}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 underline"
                    >
                        {part}
                    </a>
                );
            }
            return part;
        });
    };

    const displayedNotes = showAllNotes ? notes : notes.slice(0, 5);

    return (
        <div className="space-y-3 sm:space-y-4">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 sm:gap-0">
                <h3 className="text-base sm:text-lg font-medium">Class Notes</h3>
                <button
                    onClick={() => setIsAdding(true)}
                    className="inline-flex items-center px-2.5 sm:px-3 py-1.5 sm:py-2 bg-gradient-to-r from-cc_brand to-blue-600 text-white text-xs sm:text-sm rounded-lg hover:from-blue-600 hover:to-blue-700 transition-all duration-200 shadow-md hover:shadow-lg w-full sm:w-auto justify-center"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" className="sm:w-4 sm:h-4 mr-1.5 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                        <polyline points="14,2 14,8 20,8"></polyline>
                        <line x1="16" y1="13" x2="8" y2="13"></line>
                        <line x1="16" y1="17" x2="8" y2="17"></line>
                        <polyline points="10,9 9,9 8,9"></polyline>
                    </svg>
                    <span>Add Note</span>
                </button>
            </div>

            {isAdding && (
                <div className="bg-gray-50 p-4 rounded-lg space-y-3 border border-gray-200">
                    <div className="flex justify-between items-center">
                        <h4 className="font-medium text-gray-900">Add New Note</h4>
                        <button
                            onClick={() => {
                                setIsAdding(false);
                                setNewNote({ notes: '', files: [] });
                                setNoteError(false);
                            }}
                            className="text-gray-500 hover:text-gray-700"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <line x1="18" y1="6" x2="6" y2="18"></line>
                                <line x1="6" y1="6" x2="18" y2="18"></line>
                            </svg>
                        </button>
                    </div>

                    <textarea
                        value={newNote.notes}
                        onChange={(e) => {
                            setNewNote({...newNote, notes: e.target.value});
                            if (noteError) setNoteError(false);
                        }}
                        placeholder="Enter class notes... (You can include links like https://example.com)"
                        className={`w-full p-3 border rounded-md focus:outline-none focus:ring-2 ${
                            noteError 
                                ? 'border-red-500 focus:ring-red-500 focus:border-red-500' 
                                : 'border-gray-300 focus:ring-cc_brand focus:border-transparent'
                        }`}
                        rows={4}
                    />
                    {noteError && (
                        <p className="text-sm text-red-600 mt-1">Please fill in the note textbox</p>
                    )}

                    <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700">
                            Upload Files (Max 5MB per file)
                        </label>
                        <input
                            type="file"
                            multiple
                            onChange={handleFileChange}
                            className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cc_brand"
                        />
                        {newNote.files.length > 0 && (
                            <div className="space-y-1 mt-2">
                                {newNote.files.map((file, index) => (
                                    <div key={index} className="flex items-center justify-between bg-gray-50 px-3 py-2 rounded border border-gray-200">
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-medium text-gray-900 truncate">{file.name}</p>
                                            <p className="text-xs text-gray-500">{formatFileSize(file.size)}</p>
                                        </div>
                                        <button
                                            onClick={() => setNewNote(prev => ({
                                                ...prev,
                                                files: prev.files.filter((_, i) => i !== index)
                                            }))}
                                            className="ml-3 text-red-600 hover:text-red-800 hover:bg-red-50 p-1 rounded transition-colors"
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <line x1="18" y1="6" x2="6" y2="18"></line>
                                                <line x1="6" y1="6" x2="18" y2="18"></line>
                                            </svg>
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                        <button
                            onClick={handleAddNote}
                            disabled={isUploading || !newNote.notes.trim()}
                            className="inline-flex items-center justify-center px-3 sm:px-4 py-1.5 sm:py-2 bg-gradient-to-r from-cc_brand to-blue-600 text-white text-xs sm:text-sm rounded-lg hover:from-blue-600 hover:to-blue-700 transition-all duration-200 shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto"
                        >
                            {isUploading ? (
                                <>
                                    <div className="animate-spin rounded-full h-3 w-3 sm:h-4 sm:w-4 border-b-2 border-white mr-1.5 sm:mr-2"></div>
                                    <span>Adding...</span>
                                </>
                            ) : (
                                <>
                                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" className="sm:w-4 sm:h-4 mr-1.5 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                                        <polyline points="14,2 14,8 20,8"></polyline>
                                        <line x1="16" y1="13" x2="8" y2="13"></line>
                                        <line x1="16" y1="17" x2="8" y2="17"></line>
                                        <polyline points="10,9 9,9 8,9"></polyline>
                                    </svg>
                                    <span>Add Note</span>
                                </>
                            )}
                        </button>
                        <button
                            onClick={() => {
                                setIsAdding(false);
                                setNewNote({ notes: '', files: [] });
                                setNoteError(false);
                            }}
                            className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            )}

            {loading && notes.length === 0 ? (
                <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-cc_brand mx-auto"></div>
                    <p className="text-gray-600 mt-2">Loading notes...</p>
                </div>
            ) : displayedNotes.length === 0 ? (
                <div className="text-center py-8 bg-gray-50 rounded-lg">
                    <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mx-auto text-gray-400 mb-4">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                        <polyline points="14,2 14,8 20,8"></polyline>
                        <line x1="16" y1="13" x2="8" y2="13"></line>
                        <line x1="16" y1="17" x2="8" y2="17"></line>
                        <polyline points="10,9 9,9 8,9"></polyline>
                    </svg>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No Class Notes Yet</h3>
                    <p className="text-gray-600 mb-4">
                        Start adding notes to keep track of class summaries, important points, and resources.
                    </p>
                    <button
                        onClick={() => setIsAdding(true)}
                        className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-cc_brand to-blue-600 text-white rounded-lg hover:from-blue-600 hover:to-blue-700 transition-all duration-200 shadow-md hover:shadow-lg transform hover:scale-105"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2">
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                            <polyline points="14,2 14,8 20,8"></polyline>
                            <line x1="16" y1="13" x2="8" y2="13"></line>
                            <line x1="16" y1="17" x2="8" y2="17"></line>
                            <polyline points="10,9 9,9 8,9"></polyline>
                        </svg>
                        Add First Note
                    </button>
                </div>
            ) : (
                <div className="space-y-2">
                    {displayedNotes.map((note) => (
                        <div key={note.id} className="bg-white border border-gray-200 rounded-lg p-3 hover:shadow-md transition-shadow">
                            <div className="flex items-center justify-between gap-3">
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm text-gray-900 line-clamp-2">
                                        {renderTextWithLinks(note.notes)} - <span className="text-xs text-gray-500 mt-1 inline-block">{formatDate(note.timestamp)}</span>
                                    </p>
                                    
                                </div>
                                <button
                                    onClick={() => handleDeleteNote(note)}
                                    className="text-red-600 hover:text-red-800 hover:bg-red-50 p-1.5 rounded transition-colors flex-shrink-0"
                                    title="Delete note"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <polyline points="3,6 5,6 21,6"></polyline>
                                        <path d="M19,6v14a2,2 0 0,1 -2,2H7a2,2 0 0,1 -2,-2V6m3,0V4a2,2 0 0,1 2,-2h4a2,2 0 0,1 2,2v2"></path>
                                    </svg>
                                </button>
                            </div>

                            {note.imageLinks && note.imageLinks.length > 0 && (
                                <div className="mt-2 flex flex-wrap gap-1.5">
                                    {note.imageLinks.map((imageUrl, index) => (
                                        <div key={index} className="relative">
                                            <img
                                                src={imageUrl}
                                                alt={`Note image ${index + 1}`}
                                                className="w-12 h-12 object-cover rounded border cursor-pointer hover:opacity-80 transition-opacity"
                                                onClick={() => window.open(imageUrl, '_blank')}
                                            />
                                        </div>
                                    ))}
                                </div>
                            )}

                            {note.fileLinks && note.fileLinks.length > 0 && (
                                <div className="mt-2 space-y-1">
                                    {note.fileLinks.map((file, index) => (
                                        <div key={index} className="flex items-center justify-between bg-gray-50 px-2 py-1.5 rounded border border-gray-200 hover:bg-gray-100 transition-colors">
                                            <div className="flex-1 min-w-0">
                                                <p className="text-xs font-medium text-gray-900 truncate">{file.name}</p>
                                            </div>
                                            <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                                                <span className="text-xs text-gray-500">{formatFileSize(file.size)}</span>
                                                <a
                                                    href={file.url}
                                                    download={file.name}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-blue-600 hover:text-blue-800 hover:bg-blue-50 p-1 rounded transition-colors"
                                                    title="Download file"
                                                >
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                                                        <polyline points="7 10 12 15 17 10"></polyline>
                                                        <line x1="12" y1="15" x2="12" y2="3"></line>
                                                    </svg>
                                                </a>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    ))}

                    {notes.length > 5 && (
                        <div className="text-center pt-4">
                            {!showAllNotes ? (
                                <button
                                    onClick={() => setShowAllNotes(true)}
                                    className="inline-flex items-center px-4 py-2 text-cc_brand hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-all duration-200 font-medium"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2">
                                        <polyline points="6 9 12 15 18 9"></polyline>
                                    </svg>
                                    Show All Notes ({notes.length})
                                </button>
                            ) : hasMore && (
                                <button
                                    onClick={() => loadNotes(true)}
                                    className="inline-flex items-center px-4 py-2 border border-gray-300 text-gray-700 bg-white rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-all duration-200 shadow-sm"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2">
                                        <path d="M7 13l5 5 5-5"></path>
                                        <path d="M7 6l5 5 5-5"></path>
                                    </svg>
                                    Load More Notes
                                </button>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
} 